/**
 * Simple column auto-mapper utility
 * Automatically maps columns based on header name similarity
 */

// Common patterns for each field type
const FIELD_PATTERNS = {
  partNumber: [
    'part number',
    'part #',
    'part num',
    'part no',
    'part code',
    'partnumber',
    'part_number',
    'item number',
    'item #',
    'item code',
    'product code',
    'sku',
    'material number',
    'material #',
  ],
  description: [
    'description',
    'desc',
    'product description',
    'item description',
    'part description',
    'name',
    'product name',
    'item name',
    'title',
  ],
  quantity: [
    'quantity',
    'qty',
    'amount',
    'count',
    'pieces',
    'units',
    'needed',
    'required',
    'order qty',
    'order quantity',
  ],
};

/**
 * True when a column's values look like line numbers (1, 2, 3...) rather than part
 * numbers: nearly all are whole numbers of at most 3 digits. Mapped as the part
 * number, these pad into unrelated real parts (SIDEL "22" -> UMS-00000000022).
 */
export function looksLikeLineNumbers(values: unknown[]): boolean {
  const filled = values.map((v) => String(v ?? '').trim()).filter(Boolean);
  if (filled.length < 3) return false;
  const short = filled.filter((v) => /^\d{1,3}$/.test(v)).length;
  // Tolerate a few stray rows (totals, notes) at the bottom of the list
  return short / filled.length >= 0.9;
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Score how well a header matches a pattern: 2 = exact, 1 = pattern appears as whole
 * words in the header ("Customer Part Number"), 0 = no match. Whole words only, so
 * "count" does not match "Discount"; and a short header never matches a longer
 * pattern, so "Item" does not match "item number".
 */
function scoreHeader(header: string, pattern: string): number {
  if (header === pattern) return 2;
  const wholeWords = new RegExp(
    `(?<![a-z0-9])${escapeRegExp(pattern)}(?![a-z0-9])`
  );
  return wholeWords.test(header) ? 1 : 0;
}

/**
 * Find the best-scoring unused column for a field (1-based), or 0 if none match.
 * Ties go to the leftmost column.
 */
function findBestColumnMatch(
  headers: (string | null)[],
  patterns: string[],
  usedColumns: Set<number>
): number {
  let bestColumn = 0;
  let bestScore = 0;

  headers.forEach((header, i) => {
    if (!header || usedColumns.has(i + 1)) return;
    const normalizedHeader = header.toLowerCase().trim();
    const score = Math.max(
      ...patterns.map((p) => scoreHeader(normalizedHeader, p))
    );
    if (score > bestScore) {
      bestScore = score;
      bestColumn = i + 1;
    }
  });

  return bestColumn;
}

/**
 * Auto-map columns based on header names
 */
export function autoMapColumns(headers: (string | null)[]): {
  partNumber: number;
  description: number;
  quantity: number;
} {
  const usedColumns = new Set<number>();
  const fieldMappings = {
    partNumber: 0,
    description: 0,
    quantity: 0,
  };

  // Define field priority (lower number = higher priority)
  const fieldPriority = [
    { field: 'partNumber' as const, patterns: FIELD_PATTERNS.partNumber },
    { field: 'quantity' as const, patterns: FIELD_PATTERNS.quantity },
    { field: 'description' as const, patterns: FIELD_PATTERNS.description },
  ];

  // Try to map each field in priority order
  for (const { field, patterns } of fieldPriority) {
    const columnIndex = findBestColumnMatch(headers, patterns, usedColumns);
    if (columnIndex > 0) {
      fieldMappings[field] = columnIndex;
      usedColumns.add(columnIndex);
    }
  }

  // Unmatched fields take the leftmost unused columns, so no two fields share one
  let nextColumn = 1;
  for (const field of ['partNumber', 'description', 'quantity'] as const) {
    if (fieldMappings[field]) continue;
    while (usedColumns.has(nextColumn)) nextColumn++;
    fieldMappings[field] = nextColumn;
    usedColumns.add(nextColumn);
  }

  return fieldMappings;
}
